'use client';

import { useState, useEffect } from 'react';
import CompanyStory from './CompanyStory';
import LeadershipTeam from './LeadershipTeam';
import TechnicalCapabilities from './TechnicalCapabilities';
import CertificationsCompliance from './CertificationsCompliance';
import SafetyStandards from './SafetyStandards';
import MediaCoverage from './MediaCoverage';
import DownloadResources from './DownloadResources';

interface Leader {
  id: number;
  name: string;
  position: string;
  expertise: string;
  experience: string;
  image: string;
  alt: string;
  credentials: string[];
}

interface Capability {
  id: number;
  category: string;
  skills: string[];
  equipment: string[];
  icon: string;
}

interface Certification {
  id: number;
  name: string;
  issuer: string;
  validUntil: string;
  category: string;
  image: string;
  alt: string;
}

interface SafetyMetric {
  id: number;
  label: string;
  value: string;
  icon: string;
  description: string;
}

interface SafetyStandard {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface MediaItem {
  id: number;
  type: 'award' | 'publication' | 'feature';
  title: string;
  source: string;
  date: string;
  description: string;
  image: string;
  alt: string;
}

interface Resource {
  id: number;
  title: string;
  description: string;
  fileSize: string;
  icon: string;
}

export default function AboutUsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const companyStory = {
    title: "Our Journey of Excellence",
    description: "Since our founding, Ativanam Solutions has been at the forefront of infrastructure services, delivering mission-critical projects with unwavering reliability and technical expertise. Our journey reflects our commitment to engineering excellence and operational dependability.",
    foundedYear: 2010,
    milestones: [
    {
      year: 2010,
      title: "Foundation & Vision",
      description: "Established with a vision to provide world-class infrastructure services, focusing on telecom and electrical systems integration for critical projects."
    },
    {
      year: 2013,
      title: "Government Recognition",
      description: "Achieved major milestone by securing first government contract for telecom infrastructure, establishing credibility in public sector projects."
    },
    {
      year: 2016,
      title: "Technical Expansion",
      description: "Expanded service portfolio to include critical power systems and industrial automation, investing in advanced equipment and specialized training programs."
    },
    {
      year: 2019,
      title: "ISO Certifications",
      description: "Obtained ISO 9001:2015 and ISO 45001:2018 certifications, demonstrating commitment to quality management and occupational health safety standards."
    },
    {
      year: 2022,
      title: "Regional Leadership",
      description: "Established as regional leader in infrastructure services with 500+ successful projects, maintaining 98% client retention rate and zero major safety incidents."
    },
    {
      year: 2026,
      title: "Innovation & Growth",
      description: "Launched 24/7 emergency response services and predictive maintenance programs, leveraging IoT and AI technologies for proactive infrastructure management."
    }]

  };

  const leaders: Leader[] = [
  {
    id: 1,
    name: "Rajesh Kumar",
    position: "Founder & Managing Director",
    expertise: "Telecom Infrastructure & System Integration",
    experience: "25+ years in infrastructure services",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12b9c2bb3-1763293370864.png",
    alt: "Professional headshot of Indian man in navy blue suit with grey hair and confident smile",
    credentials: ["B.Tech (EEE)", "MBA", "PMP Certified"]
  },
  {
    id: 2,
    name: "Priya Sharma",
    position: "Chief Technical Officer",
    expertise: "Electrical Systems & Power Distribution",
    experience: "18+ years in critical infrastructure",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14bd1d466-1763295184837.png",
    alt: "Professional portrait of Indian woman in formal blazer with short hair in modern office",
    credentials: ["M.Tech (Power)", "Chartered Engineer", "LEED AP"]
  },
  {
    id: 3,
    name: "Arun Patel",
    position: "Head of Operations",
    expertise: "Project Management & Quality Assurance",
    experience: "20+ years in EPC projects",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1249ee08a-1763294972375.png",
    alt: "Professional photo of Indian man with beard in white shirt and glasses in office setting",
    credentials: ["B.E. (Civil)", "Six Sigma Black Belt", "NEBOSH IGC"]
  },
  {
    id: 4,
    name: "Meera Reddy",
    position: "Head of Safety & Compliance",
    expertise: "Safety Management & Regulatory Compliance",
    experience: "15+ years in safety engineering",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecb4aca8-1763296537332.png",
    alt: "Professional headshot of Indian woman in formal attire with long dark hair and warm smile",
    credentials: ["B.Tech (Safety)", "NEBOSH Diploma", "ISO Lead Auditor"]
  },
  {
    id: 5,
    name: "Vikram Singh",
    position: "Business Development Director",
    expertise: "Strategic Partnerships & Government Relations",
    experience: "22+ years in B2B sales",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee18e820-1763300297556.png",
    alt: "Professional portrait of Indian man in grey suit with short hair in corporate environment",
    credentials: ["MBA (Marketing)", "Certified Sales Professional", "Government Procurement Expert"]
  },
  {
    id: 6,
    name: "Anjali Desai",
    position: "Head of Client Services",
    expertise: "Customer Success & AMC Management",
    experience: "12+ years in service delivery",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1426d9af3-1763296752828.png",
    alt: "Professional photo of Indian woman in business casual attire with shoulder-length hair smiling",
    credentials: ["B.Tech (IT)", "ITIL Expert", "Customer Success Manager"]
  }];


  const capabilities: Capability[] = [
  {
    id: 1,
    category: "Telecom Infrastructure",
    skills: [
    "BTS installation and commissioning",
    "Fiber optic network deployment",
    "Microwave link setup and alignment",
    "Tower erection and maintenance"],

    equipment: ["Cable testers", "OTDR", "Spectrum analyzers", "Tower climbing gear"],
    icon: "SignalIcon"
  },
  {
    id: 2,
    category: "Electrical Systems",
    skills: [
    "HT/LT panel installation",
    "Power distribution design",
    "Earthing and lightning protection",
    "Energy audit and optimization"],

    equipment: ["Megger testers", "Power analyzers", "Thermal cameras", "Cable fault locators"],
    icon: "BoltIcon"
  },
  {
    id: 3,
    category: "Critical Power",
    skills: [
    "UPS system integration",
    "DG set installation and AMC",
    "Battery bank management",
    "Power quality monitoring"],

    equipment: ["Load banks", "Battery testers", "Harmonic analyzers", "Insulation testers"],
    icon: "Battery100Icon"
  },
  {
    id: 4,
    category: "Industrial Automation",
    skills: [
    "PLC programming and integration",
    "SCADA system implementation",
    "Control panel design",
    "Process automation solutions"],

    equipment: ["PLC programmers", "HMI panels", "Field instruments", "Network analyzers"],
    icon: "CogIcon"
  }];


  const certifications: Certification[] = [
  {
    id: 1,
    name: "ISO 9001:2015",
    issuer: "Bureau Veritas",
    validUntil: "Dec 2027",
    category: "Quality Management",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13cb60122-1764646558400.png",
    alt: "ISO 9001 quality management certification badge with blue and white design"
  },
  {
    id: 2,
    name: "ISO 45001:2018",
    issuer: "TUV SUD",
    validUntil: "Mar 2028",
    category: "Safety Standards",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc331da2-1764657834545.png",
    alt: "ISO 45001 occupational health and safety certification emblem"
  },
  {
    id: 3,
    name: "ISO 14001:2015",
    issuer: "SGS",
    validUntil: "Jun 2027",
    category: "Quality Management",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1177d2def-1767006872473.png",
    alt: "ISO 14001 environmental management certification logo with green elements"
  },
  {
    id: 4,
    name: "NEBOSH IGC",
    issuer: "NEBOSH UK",
    validUntil: "Lifetime",
    category: "Safety Standards",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17ac7bf71-1767945083749.png",
    alt: "NEBOSH International General Certificate safety qualification badge"
  },
  {
    id: 5,
    name: "Electrical Contractor License",
    issuer: "State Electrical Board",
    validUntil: "Dec 2026",
    category: "Regulatory Compliance",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11ea689f6-1767120202405.png",
    alt: "Official electrical contractor license certificate with government seal"
  },
  {
    id: 6,
    name: "Telecom Service Provider Registration",
    issuer: "Department of Telecommunications",
    validUntil: "Ongoing",
    category: "Regulatory Compliance",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_141715e09-1767945084888.png",
    alt: "Telecom service provider registration certificate with official stamps"
  },
  {
    id: 7,
    name: "MSME Registration",
    issuer: "Ministry of MSME",
    validUntil: "Lifetime",
    category: "Regulatory Compliance",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fae65811-1764830396492.png",
    alt: "MSME registration certificate with government of India emblem"
  },
  {
    id: 8,
    name: "National Safety Award",
    issuer: "National Safety Council",
    validUntil: "2025",
    category: "Safety Standards",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_191f2478c-1764652754772.png",
    alt: "National safety award trophy with golden finish and safety symbol"
  }];


  const safetyMetrics: SafetyMetric[] = [
  {
    id: 1,
    label: "Safety Record",
    value: "0",
    icon: "ShieldCheckIcon",
    description: "Major incidents in 5 years"
  },
  {
    id: 2,
    label: "Training Hours",
    value: "10,000+",
    icon: "AcademicCapIcon",
    description: "Annual safety training"
  },
  {
    id: 3,
    label: "Compliance Rate",
    value: "100%",
    icon: "CheckBadgeIcon",
    description: "Regulatory compliance"
  },
  {
    id: 4,
    label: "Safety Audits",
    value: "98%",
    icon: "ClipboardDocumentCheckIcon",
    description: "Average audit score"
  }];


  const safetyStandards: SafetyStandard[] = [
  {
    id: 1,
    title: "Comprehensive Risk Assessment",
    description: "Mandatory risk assessment before every project with detailed hazard identification, risk evaluation, and mitigation planning documented and reviewed.",
    icon: "ExclamationTriangleIcon"
  },
  {
    id: 2,
    title: "Personal Protective Equipment",
    description: "Strict PPE compliance with site-specific requirements including helmets, safety shoes, harnesses, gloves, and specialized equipment for high-risk activities.",
    icon: "ShieldCheckIcon"
  },
  {
    id: 3,
    title: "Emergency Response Protocols",
    description: "24/7 emergency response team with trained first responders, evacuation procedures, and coordination with local emergency services for rapid incident management.",
    icon: "BellAlertIcon"
  },
  {
    id: 4,
    title: "Regular Safety Training",
    description: "Mandatory quarterly safety training for all personnel covering equipment operation, hazard awareness, emergency procedures, and regulatory compliance updates.",
    icon: "AcademicCapIcon"
  },
  {
    id: 5,
    title: "Equipment Safety Checks",
    description: "Daily pre-operation equipment inspections with documented checklists, preventive maintenance schedules, and immediate removal of faulty equipment from service.",
    icon: "WrenchScrewdriverIcon"
  },
  {
    id: 6,
    title: "Incident Reporting System",
    description: "Transparent incident reporting with root cause analysis, corrective action implementation, and lessons learned sharing across all project teams.",
    icon: "DocumentTextIcon"
  }];


  const mediaItems: MediaItem[] = [
  {
    id: 1,
    type: "award",
    title: "Excellence in Infrastructure Services Award 2025",
    source: "Infrastructure India Magazine",
    date: "Jan 2026",
    description: "Recognized for outstanding contribution to telecom infrastructure development and maintaining highest safety standards across 100+ projects.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d6a54c5-1764680144812.png",
    alt: "Golden trophy award with infrastructure excellence engraving on marble base"
  },
  {
    id: 2,
    type: "publication",
    title: "Future of Critical Infrastructure Management",
    source: "Telecom Today",
    date: "Nov 2025",
    description: "Featured article on predictive maintenance strategies and IoT integration in telecom infrastructure, authored by our CTO Priya Sharma.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14ec2ffa0-1767945082649.png",
    alt: "Magazine cover showing telecom tower with digital network overlay and technology graphics"
  },
  {
    id: 3,
    type: "feature",
    title: "Case Study: Smart City Infrastructure Project",
    source: "Smart Cities India",
    date: "Sep 2025",
    description: "In-depth coverage of our role in implementing integrated infrastructure solutions for tier-2 city smart city project worth ₹50 crores.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_157438d8c-1767018227284.png",
    alt: "Modern smart city infrastructure with connected buildings and digital network visualization"
  },
  {
    id: 4,
    type: "award",
    title: "Best Safety Practices Award 2024",
    source: "National Safety Council",
    date: "Mar 2025",
    description: "Honored for implementing industry-leading safety protocols and achieving zero major incidents across all projects for consecutive 5 years.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ead71dd6-1765289477298.png",
    alt: "Safety award certificate with national safety council seal and safety helmet icon"
  },
  {
    id: 5,
    type: "publication",
    title: "Sustainable Infrastructure Development",
    source: "Green Building Magazine",
    date: "Jul 2025",
    description: "Published research on energy-efficient infrastructure solutions and sustainable practices in telecom and electrical installations.",
    image: "https://images.unsplash.com/photo-1635745262604-7bddf9ed9567",
    alt: "Green building with solar panels and sustainable infrastructure elements"
  },
  {
    id: 6,
    type: "feature",
    title: "Government Partnership Success Story",
    source: "Public Sector Today",
    date: "May 2025",
    description: "Featured coverage of successful completion of government telecom infrastructure project ahead of schedule with 15% cost savings.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ccbbfe1e-1764849106699.png",
    alt: "Government officials and project team celebrating successful infrastructure project completion"
  }];


  const resources: Resource[] = [
  {
    id: 1,
    title: "Company Profile",
    description: "Comprehensive overview of Ativanam Solutions including services, capabilities, and project portfolio",
    fileSize: "2.5 MB PDF",
    icon: "DocumentTextIcon"
  },
  {
    id: 2,
    title: "Capability Statement",
    description: "Detailed technical capabilities, certifications, and equipment inventory for procurement evaluation",
    fileSize: "1.8 MB PDF",
    icon: "ClipboardDocumentListIcon"
  },
  {
    id: 3,
    title: "Safety & Compliance Documentation",
    description: "Complete safety records, certifications, and regulatory compliance documentation",
    fileSize: "3.2 MB PDF",
    icon: "ShieldCheckIcon"
  },
  {
    id: 4,
    title: "Project Portfolio",
    description: "Visual showcase of completed projects with technical specifications and client testimonials",
    fileSize: "5.1 MB PDF",
    icon: "PhotoIcon"
  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-16" />
        <div className="animate-pulse">
          <div className="h-96 bg-muted" />
          <div className="max-w-7xl mx-auto px-4 py-16 space-y-8">
            <div className="h-8 bg-muted rounded w-1/3 mx-auto" />
            <div className="h-4 bg-muted rounded w-2/3 mx-auto" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) =>
              <div key={i} className="h-64 bg-muted rounded" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-16" />
      <CompanyStory story={companyStory} />
      <LeadershipTeam leaders={leaders} />
      <TechnicalCapabilities capabilities={capabilities} />
      <CertificationsCompliance certifications={certifications} />
      <SafetyStandards metrics={safetyMetrics} standards={safetyStandards} />
      <MediaCoverage mediaItems={mediaItems} />
      <DownloadResources resources={resources} />
    </div>);

}